import { ReferralVaultMeta } from '@/modules/referral/models/meta/ReferralVaultMeta';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { ReferralSettlementMeta } from '@/modules/referral/models/meta/ReferralSettlementMeta';
import { ReferralMetaFactory } from '@/modules/referral/models/meta/ReferralMetaFactory';
import { User } from '@/modules/user/models/User';
import { SelfServeBillingPlanForm } from '@/modules/estate/models/SelfServeBillingPlanForm';
import { Contact } from '@/modules/contact/models/Contact';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { ReferralStatus } from '@/modules/referral/constants/ReferralStatus';
import { Meeting } from '@/modules/meeting/models/Meeting';
import { BillingPlanMapping } from '@/modules/billing/models/BillingPlanMapping';
import { VaultBillingPlanFormFactory } from '@/modules/estate/models/VaultBillingPlanFormFactory';
import { PredefinedJurisdiction } from '@/modules/predefined/enums/PredefinedJurisdiction';
import { EstatePlanPartyType } from '@/modules/estate/enums/EstatePlanPartyType';
import { Company } from '@/modules/company/models/Company';
import { ReferralType } from '@/modules/referral/constants/ReferralType';
import { MeetingInviteeObject } from '@/modules/meeting/models/MeetingInviteeObject';
import Lang from '@/modules/core/app/services/Lang';
import { ReferralSubType } from '@/modules/referral/constants/ReferralSubType';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { VaultPlanType } from '@/modules/estate/constants/VaultPlanType';
import { PartnershipInvitationType } from '@/modules/partnershipinvitation/constants/PartnershipInvitationType';
import { Team } from '@/modules/team/models/Team';
import ReferralStatusView from '@/modules/referral/constants/ReferralStatusView';
import { Estate } from '@/modules/estate/models/Estate';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { ReferralClientTypeOption } from '@/modules/referral/constants/ReferralClientTypeOption';
import { BookingLink } from '@/modules/bookinglink/models/BookingLink';

export class Referral extends BaseEntityModel {
  id?: number;
  estateId?: number;
  userId?: number;
  teamId?: number;
  companyId?: number;
  meetingId?: number;
  type?: ReferralType;
  partnershipInvitationType?: PartnershipInvitationType;
  subType?: ReferralSubType;
  lineOfBusiness?: string;
  jurisdictionId?: number;
  billingPlanMappingId?: number;
  createdByUserId?: number;
  metaType?: string;
  status?: ReferralStatus;
  isFree?: boolean;
  isEmployee?: boolean;
  hasAdvisorAcknowledgment?: boolean;
  archivedAt?: string;

  meta?: Relationship<ReferralVaultMeta | ReferralSettlementMeta> | null;

  user?: Relationship<User> | null;
  estate?: Relationship<Estate> | null;
  meeting?: Relationship<Meeting> | null;
  createdByUser?: Relationship<User | null>;
  company?: Relationship<Company | null>;
  billingPlanMapping?: Relationship<BillingPlanMapping | null>;
  billingPlan?: SelfServeBillingPlanForm;

  partnerReferral?: Relationship<Referral | null>;
  deceasedContact?: Relationship<Contact | null>;
  team?: Relationship<Team | null>;
  bookingLink?: Relationship<BookingLink | null>;

  billingPlanType?: string;
  billingPlanId?: number;

  showBackToSolutionsButton?: boolean;

  constructor(model: Partial<Referral> = {}) {
    super(model);
    this.id = model.id;
    this.estateId = model.estateId;
    this.userId = model.userId;
    this.teamId = model.teamId;
    this.companyId = model.companyId;
    this.meetingId = model.meetingId;
    this.jurisdictionId = model.jurisdictionId;
    this.billingPlanMappingId = model.billingPlanMappingId;
    this.createdByUserId = model.createdByUserId;
    this.metaType = model.metaType;
    this.isFree = model.isFree;
    this.isEmployee = model.isEmployee;
    this.type = model.type;
    this.subType = model.subType;
    this.lineOfBusiness = model.lineOfBusiness;
    this.status = model.status;
    this.archivedAt = model.archivedAt;
    this.hasAdvisorAcknowledgment = model.hasAdvisorAcknowledgment;
    this.meta = ReferralMetaFactory.build(model);
    this.estate = Estate.mapModelOrNull(model.estate);
    this.user = User.mapModelOrNull(model.user);
    this.meeting = Meeting.mapModelOrNull(model.meeting);
    this.createdByUser = User.mapModelOrNull(model.createdByUser);
    this.company = Company.mapModelOrNull(model.company);
    this.partnerReferral = Referral.mapModelOrNull(model.partnerReferral);
    this.billingPlanMapping = BillingPlanMapping.mapModelOrNull(model.billingPlanMapping);
    this.deceasedContact = Contact.mapModelOrNull(model.deceasedContact);
    this.team = Team.mapModelOrNull(model.team);
    this.bookingLink = BookingLink.mapModelOrNull(model.bookingLink);
    this.billingPlan = model.billingPlan ?? this.buildBillingPlan();

    this.showBackToSolutionsButton = !!model.showBackToSolutionsButton;
    this.partnershipInvitationType = model.partnershipInvitationType as PartnershipInvitationType;
  }

  setEstate(value: Estate): Referral {
    this.estate = value;
    this.estateId = value.id;

    if (this.isCouples) {
      this.partnerReferral?.setEstate(value.partnerEstate!);
    }

    return this;
  }

  setSubType(value: ReferralSubType): void {
    this.subType = value;
  }

  setIsEmployee(value: boolean): void {
    this.isEmployee = value;
  }

  setPartnershipInvitationType(value?: PartnershipInvitationType): void {
    this.partnershipInvitationType = value ?? PartnershipInvitationType.CLIENT;
  }

  setAdvisorlessFlow(): void {
    this.subType = ReferralSubType.ADVISORLESS;
  }

  setAdvisorOptionalFlow(): void {
    this.subType = ReferralSubType.ADVISOR_OPTIONAL;
  }

  setLineOfBusiness(value?: string): void {
    this.lineOfBusiness = value;
  }

  get isSelfReferralAdvisorless(): boolean {
    return this.isSelfReferral && this.isAdvisorlessSubType;
  }

  getPrimaryReferralFormId(): number {
    if (this.isCouples) {
      return this.isCouplesPrimary ? this.id! : this.meta.partnerReferralId!;
    }

    return this.id!;
  }

  setShowBackToSolutionsButton(value: boolean): void {
    this.showBackToSolutionsButton = value;
  }

  get isSelfReferral(): boolean {
    return this.type === ReferralType.SELF_REFERRAL;
  }

  get isFinancialAdvisorRequired(): boolean {
    if (this.isAdvisorOptionalSubType || this.isAdvisorlessSubType) {
      return false;
    }

    return true;
  }

  get isInvited(): boolean {
    return this.status === ReferralStatus.INVITED;
  }

  get isPending(): boolean {
    return this.status === ReferralStatus.PENDING;
  }

  get isFreeInvitedReferral(): boolean {
    return !!this.id && !!this.isFree && this.isInvited;
  }

  get isFinancialAdvisorReferral(): boolean {
    return this.type === ReferralType.FINANCIAL_ADVISOR_REFERRAL;
  }

  get isAdvisorlessSubType(): boolean {
    return this.subType === ReferralSubType.ADVISORLESS;
  }

  get isAdvisorOptionalSubType(): boolean {
    return this.subType === ReferralSubType.ADVISOR_OPTIONAL;
  }

  get estatePlanPartyType(): EstatePlanPartyType {
    return this.isCouples ? EstatePlanPartyType.COUPLE : EstatePlanPartyType.INDIVIDUAL;
  }

  get guestEmailsForReferallBooking(): string[] {
    const emails: string[] = [];

    if (this.createdByUser?.email) {
      emails.push(this.createdByUser?.email);
    }

    if (this.partnerReferral?.user?.email) {
      emails.push(this.partnerReferral.user.email!);
    }

    return emails;
  }

  get statusViewEnum(): ReferralStatusView {
    if (this.status === ReferralStatus.PENDING) {
      return ReferralStatusView.NOT_INVITED;
    }

    return ReferralStatusView.INVITED;
  }

  getMeetingName(): string {
    let name: string = '';

    if (this.user) {
      name = `${this.user!.name}`;
    }

    if (this.partnerReferral?.user) {
      name =
        name === ''
          ? `${this.partnerReferral!.user!.name}`
          : `${name} & ${this.partnerReferral!.user!.name}`;
    }

    return name;
  }

  get getInviteesForReferallBooking(): MeetingInviteeObject[] {
    const invitees: MeetingInviteeObject[] = [];

    if (this.user) {
      invitees.push(MeetingInviteeObject.mapModel({ user: this.user }));
    }

    if (this.createdByUser) {
      invitees.push(MeetingInviteeObject.mapModel({ user: this.createdByUser }));
    }

    if (this.partnerReferral?.user) {
      invitees.push(MeetingInviteeObject.mapModel({ user: this.partnerReferral.user }));
    }

    return invitees;
  }

  setUserLocaleForSelfReferral(): void {
    if (this.user) {
      this.user.locale = Lang.locale;
    }

    if (this.partnerReferral?.user) {
      this.partnerReferral.user.locale = Lang.locale;
    }
  }

  setAsIndividualForStandardPlan(plan: SelfServeBillingPlanForm): Referral[] {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const firstReferral: Referral = this;
    const secondReferral: Referral = this.partnerReferral!;

    firstReferral.partnerReferral = null;
    secondReferral.partnerReferral = null;

    firstReferral.partnerReferral = null;
    (firstReferral.meta as ReferralVaultMeta).partnerReferralId = null;
    secondReferral.partnerReferral = null;
    (secondReferral.meta as ReferralVaultMeta).partnerReferralId = null;

    firstReferral.setBillingPlan(plan);
    secondReferral.setBillingPlan(plan);

    return [firstReferral, secondReferral];
  }

  setAsVault(): void {
    this.metaType = ModelType.REFERRAL_VAULT_META;
    this.meta = new ReferralVaultMeta();
  }

  setAsSettlement(): void {
    this.metaType = ModelType.REFERRAL_SETTLEMENT_META;
    this.meta = new ReferralSettlementMeta();
    this.setAsIndividual();
  }

  initDeceasedContact(): void {
    this.deceasedContact = Contact.mapModel({});
  }

  setAsIndividual(): void {
    this.user = User.mapModel({
      contact: Contact.mapModel({})
    });
    this.partnerReferral = null;
  }

  setAsCouples(): void {
    this.user = User.mapModel({
      contact: Contact.mapModel({})
    });
    this.partnerReferral = Referral.mapModel({
      user: User.mapModel({
        contact: Contact.mapModel({})
      }),
      teamId: this.teamId,
      status: this.status
    });

    if (this.isMetaReferralVault) {
      this.partnerReferral.setAsVault();
    } else if (this.isMetaSettlementVault) {
      this.partnerReferral.setAsSettlement();
    }

    if (this.subType) {
      this.partnerReferral.setSubType(this.subType);
    }

    this.partnerReferral.partnershipInvitationType = this.partnershipInvitationType;
    this.partnerReferral.lineOfBusiness = this.lineOfBusiness;
  }

  setBillingPlan(plan?: SelfServeBillingPlanForm): void {
    if (!plan) {
      return;
    }
    this.billingPlan = new SelfServeBillingPlanForm(JSON.parse(JSON.stringify(plan)));
    this.billingPlan!.setIsRecommended(true);
    this.billingPlan!.setPrices(plan.price, plan.yearlyPrice);
    this.billingPlanType = this.billingPlan.type;
    // @ts-ignore
    this.billingPlanId = plan.id;
  }

  setMeeting(value: Meeting): void {
    this.meeting = value;
    this.setMeetingId(value.id!);
  }

  setMeetingId(value: number): void {
    this.meetingId = value;

    if (this.isCouples && this.partnerReferral) {
      this.partnerReferral.meetingId = value;
    }
  }

  setStatus(value: ReferralStatus): void {
    this.status = value;

    if (this.isCouples && this.partnerReferral) {
      this.partnerReferral.status = value;
    }
  }

  get isQuebec(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.QUEBEC['id'];
  }

  get clientName(): string {
    return this.user!.name;
  }

  get partnerClientName(): string {
    return this.partnerReferral!.user!.name;
  }

  get planName(): string {
    return this.billingPlan!.name!;
  }

  get isSelfServePlan(): boolean {
    return !!this.billingPlanMapping?.isSelfServePlan;
  }

  get isMetaReferralVault(): boolean {
    return this.metaType === ModelType.REFERRAL_VAULT_META;
  }

  get isMetaSettlementVault(): boolean {
    return this.metaType === ModelType.REFERRAL_SETTLEMENT_META;
  }

  get isCouples(): boolean {
    return !!this.partnerReferral || !!this.meta?.partnerReferralId;
  }

  get isIndividual(): boolean {
    return !this.isCouples;
  }

  get clientTypeKey(): string {
    return this.isCouples
      ? ReferralClientTypeOption.COUPLES.key
      : ReferralClientTypeOption.INDIVIDUAL.key;
  }

  get isCouplesPrimary(): boolean {
    const meta: ReferralVaultMeta | null = this.meta as ReferralVaultMeta | null;

    return !!meta?.isPrimary;
  }

  get isCouplesSecondary(): boolean {
    return this.isCouples && !this.isCouplesPrimary;
  }

  get requiresOnboardingLegalContest(): boolean {
    return this.isFinancialAdvisorReferral && this.isCouplesSecondary;
  }

  get referredPlanName(): string | undefined {
    if (!this.billingPlanMapping?.planId) {
      return undefined;
    }

    switch (this.billingPlanMapping.planId) {
      case BillingPlanId.INDIVIDUAL_STANDARD_PLAN:
        return Lang.t('standardplan.online-will');
      case BillingPlanId.INDIVIDUAL_BASIC_PLAN:
      case BillingPlanId.COUPLES_BASIC_PLAN:
        return Lang.t('standardplan.basic');
      default:
        return undefined;
    }
  }

  get isUpgradeFromSelfServe(): boolean {
    if (!this.billingPlanType) {
      return false;
    }
    return (
      this.isSelfServePlan &&
      [VaultPlanType.COMPLETE.key, VaultPlanType.PROFESSIONAL.key].includes(this.billingPlanType)
    );
  }

  buildBillingPlan(): SelfServeBillingPlanForm | undefined {
    if (!this.billingPlanMapping) {
      return;
    }

    return VaultBillingPlanFormFactory.build(
      this.billingPlanMapping!.planId,
      1000,
      25
    )?.setIsRecommended(true);
  }

  getDisplayRelationshipTypeForEstateAccount(estateAccount: EstateAccount): string {
    const referralEstateAccounts: EstateAccount[] = this.estate?.estateAccounts ?? [];
    const partnerReferralEstateAccounts: EstateAccount[] =
      this.partnerReferral?.estate?.estateAccounts ?? [];

    const isRelatedToBoth: boolean =
      referralEstateAccounts.some(
        (account: EstateAccount) => account.userId === estateAccount.userId
      ) &&
      partnerReferralEstateAccounts.some(
        (account: EstateAccount) => account.userId === estateAccount.userId
      );

    if (!!isRelatedToBoth || !this.estate || !this.partnerReferral?.estate) {
      return estateAccount.displayContactRelationshipType;
    }

    const linkedReferralUserName: string =
      [this.estate!, this.partnerReferral!.estate!].find(
        (estate: Estate) => estate.id === estateAccount.estateId
      )?.contact?.fullName ?? '';

    return Lang.t('contact.relationship-someone', {
      name: linkedReferralUserName,
      relationship: estateAccount.displayContactRelationshipType
    });
  }

  get shouldShowAdvisorInputReviewPage(): boolean {
    if (!this.estate?.isShell) {
      return false;
    }

    const hasDateOfBirth: boolean = !!this.estate.contact?.dateOfBirth;
    const hasCompleteAddress: boolean = !!this.estate.contact?.address?.isComplete;
    const hasEstateAccounts: boolean = !!this.estate.estateAccounts?.filter(
      (estateAccount: EstateAccount) =>
        estateAccount.isVisibleInReferralAdvisorReviewInputPage(this)
    ).length;
    const hasAssets: boolean = !!this.estate.assets?.length;
    const hasLiabilities: boolean = !!this.estate.liabilities?.length;
    const hasDocuments: boolean = this.estate.documents.hasItems;

    return (
      hasDateOfBirth ||
      hasCompleteAddress ||
      hasEstateAccounts ||
      hasAssets ||
      hasLiabilities ||
      hasDocuments
    );
  }
}
